import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { CardContent, Typography, Box, Chip, Divider, useTheme, useMediaQuery, CardActionArea } from '@mui/material';
import { GlassCard } from '../../../components/ui';
import { useTranslation } from 'react-i18next';
import ItemIcon from '../../../components/ItemIcon';
import type { WikiNewItem } from '../../../types';
import { getRankColor, getGradeColor, ACCENT_COLORS, BG_COLORS, TEXT_COLORS } from '../../../utils/colors';
import { WIKI_CARD } from '../../../utils/responsive';
import { shouldDisplayField } from '../../../config/wikiDisplayConfig';

interface WikiItemCardProps {
  item: WikiNewItem;
  source: string;
  sourceChain?: (string | undefined | null)[];
  typeId?: number;
  subtypeId?: number;
  wikiSubCategory?: number;
  // Skills-specific props
  raceId?: number;
  skillTypeId?: number;
  classId?: number;
  professionId?: number;
  onPreview?: (itemId: number, detailUrl: string) => void;
  /** Карточка открыта в боковой панели (десктоп) — подсвечиваем, как выбранную строку в поиске вики. */
  selected?: boolean;
}

const getItemCharacteristics = (
  item: WikiNewItem,
  source: string,
  sourceChain: (string | undefined | null)[],
  t: (key: string) => string
) => {
  const characteristics: Array<{ label: string; value: string | number }> = [];

  // Helper to check if field should be displayed
  const shouldShow = (field: Parameters<typeof shouldDisplayField>[0]) =>
    shouldDisplayField(field, sourceChain);

  // Level information (prioritize wikiItemLevel over legacy level)
  const itemLevel = item.wikiItemLevel || item.level;
  if (itemLevel && shouldShow('level')) {
    characteristics.push({ label: t('characteristics.level'), value: itemLevel });
  }

  // Crystal type information
  if (item.crystalType && item.crystalType !== 'NONE' && shouldShow('crystalType')) {
    characteristics.push({ label: t('characteristics.crystal'), value: item.crystalType });
  }

  // Attack stats
  if (item.patk && item.patk > 0 && shouldShow('patk')) {
    characteristics.push({ label: t('characteristics.physicalAttack'), value: item.patk });
  }
  if (item.matk && item.matk > 0 && shouldShow('matk')) {
    characteristics.push({ label: t('characteristics.magicalAttack'), value: item.matk });
  }
  if (item.patkSpd && item.patkSpd > 0 && shouldShow('patkSpd')) {
    characteristics.push({ label: t('characteristics.attackSpeed'), value: item.patkSpd });
  }
  if (item.weaponType && item.weaponType !== 'NONE' && shouldShow('weaponType')) {
    characteristics.push({ label: t('characteristics.weaponType'), value: item.weaponType });
  }

  // Defense stats
  if (item.pdef && item.pdef > 0 && shouldShow('pdef')) {
    characteristics.push({ label: t('characteristics.physicalDefense'), value: item.pdef });
  }
  if (item.mdef && item.mdef > 0 && shouldShow('mdef')) {
    characteristics.push({ label: t('characteristics.magicalDefense'), value: item.mdef });
  }

  // HP/MP
  if (item.maxHp && item.maxHp > 0 && shouldShow('maxHp')) {
    characteristics.push({ label: 'HP', value: `+${item.maxHp}` });
  }
  if (item.maxMp && item.maxMp > 0 && shouldShow('maxMp')) {
    characteristics.push({ label: 'MP', value: `+${item.maxMp}` });
  }

  // Armor type
  if (item.armorType && item.armorType !== 'NONE' && shouldShow('armorType')) {
    characteristics.push({ label: t('characteristics.armorType'), value: item.armorType });
  }

  // Quality type
  if (item.qualityType && item.qualityType !== 'NORMAL' && item.qualityType !== 'NONE' && shouldShow('qualityType')) {
    characteristics.push({ label: t('characteristics.grade'), value: item.qualityType });
  }

  // Limit to 4 most important characteristics to avoid overcrowding
  return characteristics.slice(0, 4);
};

const WikiItemCard: React.FC<WikiItemCardProps> = ({
  item,
  source,
  sourceChain = [],
  typeId,
  subtypeId,
  wikiSubCategory,
  raceId,
  skillTypeId,
  classId,
  professionId,
  onPreview,
  selected = false
}) => {
  const { t } = useTranslation('wiki');
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const iconSize = isMd ? WIKI_CARD.iconSize.md : isSm ? WIKI_CARD.iconSize.sm : WIKI_CARD.iconSize.xs;

  // Build RESTful URL for item detail page
  const getItemPath = () => {
    let path = `/wiki/${source}`;

    if (source === 'npcs') {
      // NPCs: /wiki/npcs/subcategories/{id}/details/{itemId}
      if (wikiSubCategory) {
        path += `/subcategories/${wikiSubCategory}`;
      }
    } else if (source === 'skills') {
      // Skills: /wiki/skills/races/{raceId}/types/{skillTypeId}/classes/{classId}/professions/{professionId}/details/{itemId}
      if (raceId) {
        path += `/races/${raceId}`;
        if (skillTypeId) {
          path += `/types/${skillTypeId}`;
          if (classId) {
            path += `/classes/${classId}`;
            if (professionId) {
              path += `/professions/${professionId}`;
            }
          }
        }
      }
    } else {
      // Armors/Weapons/Items: /wiki/source/types/{typeId}/subtypes/{subtypeId}/details/{itemId}
      if (typeId) {
        path += `/types/${typeId}`;
        if (subtypeId) {
          path += `/subtypes/${subtypeId}`;
        }
      }
    }

    // Always end with /details/{id}
    path += `/details/${item.id}`;
    return path;
  };


  return (
    <GlassCard
      sx={{
        height: '100%',
        ...(selected && { borderColor: ACCENT_COLORS.PRIMARY, background: BG_COLORS.GLASS_LIGHTER }),
        '&:hover': {
          transform: 'translateY(-4px) translateZ(0)',
          borderColor: selected ? ACCENT_COLORS.PRIMARY : 'rgba(185, 28, 28, 0.6)',
          background: BG_COLORS.GLASS_LIGHTER,
          boxShadow: 'none',
        },
      }}
    >
      <CardActionArea
        {...(onPreview
          ? { onClick: () => onPreview(item.id, getItemPath()) }
          : { component: RouterLink, to: getItemPath() }
        )}
        sx={{
          height: '100%',
          '& .MuiCardActionArea-focusHighlight': {
            background: 'transparent',
          },
        }}
      >
        <CardContent sx={{ textAlign: 'center', p: WIKI_CARD.padding }}>
        {/* Item Icon */}
        <Box sx={{ mb: WIKI_CARD.iconMargin, display: 'flex', justifyContent: 'center' }}>
          <ItemIcon
            icon={item.icon}
            iconPanel={item.iconPanel}
            name={item.name}
            size={iconSize}
          />
        </Box>

        {/* Item Name */}
        <Typography
          variant="subtitle1"
          component="h3"
          sx={{
            fontWeight: 600,
            mb: { xs: 0.5, sm: 1 },
            fontSize: WIKI_CARD.titleFontSize,
            lineHeight: 1.3,
            minHeight: WIKI_CARD.titleMinHeight,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {item.name}
          {/* Доп. название из клиента («[MA]») — приходит только в списках, у большинства предметов null */}
          {item.additionalName && (
            <Box component="span" sx={{ ml: 0.5, fontWeight: 500, color: TEXT_COLORS.SECONDARY }}>
              {item.additionalName}
            </Box>
          )}
        </Typography>

        {/* Short Description */}
        {item.shortDesc && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: WIKI_CARD.shortDescLineClamp,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              fontSize: WIKI_CARD.shortDescFontSize,
              lineHeight: 1.4,
              mb: { xs: 0.5, sm: 1 },
              fontStyle: 'italic',
              opacity: 0.85,
            }}
            dangerouslySetInnerHTML={{
              __html: item.shortDesc.replace(/\\n/g, '<br/>')
            }}
          />
        )}

        {/* Item Details */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: WIKI_CARD.gap, mt: { xs: 0.5, sm: 1 } }}>
          {/* Rank Chip */}
          {item.rank && item.rank !== 'NONE' && (
            <Chip
              label={item.rank}
              size="small"
              sx={{
                background: getRankColor(item.rank),
                color: 'white',
                fontWeight: 600,
                fontSize: WIKI_CARD.chipFontSize,
                height: WIKI_CARD.chipHeight,
                boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                border: 'none',
                '& .MuiChip-label': {
                  px: { xs: 1, sm: 1.5 },
                  py: 0,
                  fontSize: 'inherit',
                  textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                }
              }}
            />
          )}

          {/* Grade Chip (legacy support) */}
          {item.grade && (
            <Chip
              label={`Grade ${item.grade}`}
              size="small"
              sx={{
                backgroundColor: getGradeColor(item.grade),
                color: 'white',
                fontWeight: 600,
                fontSize: WIKI_CARD.chipFontSize,
                height: WIKI_CARD.chipHeight,
              }}
            />
          )}

          {/* Type Information */}
          {item.wikiType && item.wikiType !== 'NONE' && (
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: WIKI_CARD.detailsFontSize }}>
              {item.wikiType}
              {item.wikiSubType && item.wikiSubType !== 'NONE' && item.wikiSubType !== item.wikiType && ` • ${item.wikiSubType}`}
            </Typography>
          )}

          <Divider sx={{ my: WIKI_CARD.gap, opacity: 0.6 }} />

          {/* Characteristics Grid */}
          {getItemCharacteristics(item, source, sourceChain, t).length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.15, sm: 0.25 } }}>
              {getItemCharacteristics(item, source, sourceChain, t).map((char, index) => (
                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: WIKI_CARD.detailsFontSize }}>
                    {char.label}:
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: WIKI_CARD.detailsFontSize,
                      fontWeight: 600,
                      color: TEXT_COLORS.PRIMARY
                    }}
                  >
                    {char.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </CardContent>
      </CardActionArea>
    </GlassCard>
  );
};

export default WikiItemCard;